import { Loader, Text } from '@mantine/core'
import { FileWithPath } from '@mantine/dropzone'
import { UseFormReturnType } from '@mantine/form'
import { modals } from '@mantine/modals'
import DropZoneFileUploaderComponent from '@v2/commons/components/DropZoneFileUploader/dropZoneFileUploader.component'
import PreviewAttachments from '@v2/commons/components/UploadedAttachmentsPreview/PreviewAttachments.component'
import useDeleteAttachment from '@v2/commons/hooks/fileUpload/useDeleteAttachment.hook'
import useFormAttachmentsUpload from '@v2/commons/hooks/fileUpload/useFormAttachmentUpload.hook'
import { handleMutationError } from '@v2/commons/utils/errorHandling/handleMutationError'
import { useState } from 'react'

import { type FormEscalationAttachments } from '../../escalation.types'
import { MutateEscalationData } from '../../escalation.types'

const UploadEscalationAttachments = ({
  updateEscalationForm,
  disabled = false,
}: {
  updateEscalationForm: UseFormReturnType<MutateEscalationData>
  disabled?: boolean
}) => {
  const { isFetching, submitAttachments, hasViewAttachmentsPolicyEnabled } =
    useFormAttachmentsUpload()
  const { deleteAttachment, deleteMutationStatus } = useDeleteAttachment()
  const [erros, setErros] = useState<FormEscalationAttachments>([])

  const findDuplicates = (newFileName: string) => {
    const currentFiles = updateEscalationForm.getValues().formFields?.files
    const duplicate = currentFiles?.find((file) => file.name === newFileName)
    if (duplicate) {
      return true
    }
    return false
  }

  const handleDrop = async (files: FileWithPath[]) => {
    const { data } = await submitAttachments(files)
    let uploadedFiles: FormEscalationAttachments = []
    let filesWithErros: FormEscalationAttachments = []
    data?.forEach((uploadedFile, idx) => {
      const { url, success, error } = uploadedFile
      const { name, size, type } = files[idx]
      const isDuplicate = findDuplicates(name)
      if (success && !isDuplicate) {
        const id = new URL(url!).pathname.split('/')[1]
        uploadedFiles.push({ url: url!, name: name, size: size, type, id })
      } else {
        filesWithErros.push({
          name,
          size,
          type,
          error: error ?? 'This file already exists!',
        })
      }
    })
    updateEscalationForm.setFieldValue('formFields.files', [
      ...(updateEscalationForm.getValues().formFields?.files ?? []),
      ...uploadedFiles,
    ])
    setErros((prev) => [...prev, ...filesWithErros])
  }

  const filePreview = updateEscalationForm.getValues()?.formFields?.files ?? []

  const handleRemoveFileWithErrors = (idx: number) => {
    setErros((prev) => {
      const arrCopy = [...prev]
      arrCopy.splice(idx, 1)
      return arrCopy
    })
  }

  const handleDeleteFile = (idx: number, id?: string) => {
    deleteAttachment(id!)
      .unwrap()
      .then(() => updateEscalationForm.removeListItem('formFields.files', idx))
      .catch((err) =>
        handleMutationError(
          err,
          'failed to delete file',
          () => modals.closeAll(),
          'Close',
        ),
      )
  }

  return (
    <>
      <DropZoneFileUploaderComponent
        disabled={disabled}
        mt={64}
        onReject={() => null}
        onDrop={handleDrop}
        isLoading={isFetching}
      />
      {erros.length > 0 && (
        <Text fz={16} fw={700} lh="20px" mt={32}>
          Erros
        </Text>
      )}
      <PreviewAttachments
        previewFiles={erros}
        onDelete={handleRemoveFileWithErrors}
      />
      {filePreview.length > 0 && (
        <Text fz={16} fw={700} mt={20}>
          Uploaded
        </Text>
      )}
      {deleteMutationStatus.isLoading && <Loader mt={24} />}
      <PreviewAttachments
        previewFiles={filePreview}
        onDelete={handleDeleteFile}
        hasViewAttachmentsPolicyEnabled={hasViewAttachmentsPolicyEnabled}
      />
    </>
  )
}

export default UploadEscalationAttachments
